// ======================================
// Locations Page Module
// ======================================

import { icons, renderProjectCard } from '../components.js';
import { locationsAPI, portfolioAPI } from '../lib/api.js';

// Locations Overview Page
export function renderLocationsPage() {
  return `
    <section class="page-header">
      <div class="container">
        <div class="page-header-content">
          <h1 class="page-title">Our Locations</h1>
          <p class="page-subtitle">
            Premium interior design services across South India. 
            Find an experience center near you.
          </p>
        </div>
      </div>
    </section>

    <section class="section" style="background-color: var(--color-base);">
      <div class="container">
        <div id="locations-grid-container" class="locations-grid">
            <div class="loading-spinner"></div>
        </div>
      </div>
    </section>

    <!-- Service Areas Summary -->
    <section class="section" style="background-color: var(--color-surface);">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">We Serve 500+ Homes Across</h2>
        </div>
        <div id="areas-summary-container" class="areas-summary">
             <div class="loading-spinner"></div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Don't See Your Location?</h2>
          <p class="cta-subtitle">
            We may still be able to serve you. Contact us to check availability.
          </p>
          <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
            Get in Touch
          </button>
        </div>
      </div>
    </section>
  `;
}

export async function initLocationsPage() {
  try {
    const response = await locationsAPI.list();
    const locations = Array.isArray(response) ? response : (response.data || []);

    const gridContainer = document.getElementById('locations-grid-container');
    const areasContainer = document.getElementById('areas-summary-container');

    if (locations.length === 0) {
      gridContainer.innerHTML = '<p>No locations found.</p>';
      areasContainer.innerHTML = '';
      return;
    }

    // Render Locations Grid
    if (gridContainer) {
      gridContainer.innerHTML = locations.map(loc => `
                <a href="/locations/${loc.slug}" class="location-card">
                  <div class="location-card-img-wrapper">
                    <img src="${loc.image || 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400'}" alt="${loc.name}" class="location-card-img" onerror="this.src='https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400'">
                    <div class="location-card-overlay">
                      <span class="location-project-count">${loc.project_count || 0}+ Projects</span>
                    </div>
                  </div>
                  <div class="location-card-body">
                    <h3 class="location-card-title">${loc.name}</h3>
                    <p class="location-card-areas">${(loc.areas || []).slice(0, 4).join(', ')}${(loc.areas || []).length > 4 ? '...' : ''}</p>
                    <span class="location-card-link">
                      View Details ${icons.arrowRight}
                    </span>
                  </div>
                </a>
            `).join('');
    }

    // Render Areas Summary
    if (areasContainer) {
      areasContainer.innerHTML = locations.map(loc => `
                <div class="area-group">
                  <h3 class="area-group-title">${loc.name}</h3>
                  <div class="area-tags">
                    ${(loc.areas || []).map(area => `<span class="area-tag">${area}</span>`).join('')}
                  </div>
                </div>
            `).join('');
    }

  } catch (error) {
    console.error('Failed to load locations:', error);
    document.getElementById('locations-grid-container').innerHTML = '<p class="error-message">Failed to load locations. Please try again later.</p>';
  }
}

// Location Detail Page (for local SEO)
export function renderLocationDetailPage(params) {
  return `
    <div id="location-detail-container">
        <section class="page-header">
            <div class="container">
                <div class="page-header-content">
                    <h1 class="page-title">Loading Location...</h1>
                </div>
            </div>
        </section>
    </div>
    `;
}

export async function initLocationDetailPage(params) {
  const container = document.getElementById('location-detail-container');
  if (!container) return;

  try {
    const location = await locationsAPI.get(params.slug);

    if (!location || location.error) {
      throw new Error('Location not found');
    }

    // Fetch projects dynamically
    let localProjects = [];
    try {
      const response = await portfolioAPI.list(); // Fetch all public portfolio items
      const allProjects = Array.isArray(response) ? response : (response.data || []);

      localProjects = allProjects
        .filter(p => {
          const pLoc = (p.city || p.location || '').toLowerCase();
          return pLoc.includes(location.name.toLowerCase()) ||
            (location.areas || []).some(area => pLoc.includes(area.toLowerCase()));
        })
        .map(p => ({
          ...p,
          image: p.featured_image || p.image, // Map featured_image to image
          location: p.city || p.location,     // Map city to location
          bhk: p.category || p.property_type  // Map category to bhk/type
        }));
    } catch (e) {
      console.error('Failed to load projects for location:', e);
    }

    container.innerHTML = `
        <section class="page-header" style="background-image: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${location.image || 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=1200'}'); background-size: cover; background-position: center;">
          <div class="container">
            <div class="page-header-content" style="color: white;">
              <h1 class="page-title" style="color: white;">Interior Design in ${location.name}</h1>
              <p class="page-subtitle" style="color: rgba(255,255,255,0.9);">
                ${location.project_count || 0}+ happy homes and counting. Premium interior design services 
                across ${(location.areas || []).length} localities in ${location.name}.
              </p>
            </div>
          </div>
        </section>

        <!-- Local Stats -->
        <section class="section" style="background-color: var(--color-surface);">
          <div class="container">
            <div class="local-stats">
              <div class="local-stat">
                <div class="local-stat-value">${location.project_count || 0}+</div>
                <div class="local-stat-label">Projects Completed</div>
              </div>
              <div class="local-stat">
                <div class="local-stat-value">${(location.areas || []).length}</div>
                <div class="local-stat-label">Areas Served</div>
              </div>
              <div class="local-stat">
                <div class="local-stat-value">10</div>
                <div class="local-stat-label">Year Warranty</div>
              </div>
              <div class="local-stat">
                <div class="local-stat-value">45</div>
                <div class="local-stat-label">Day Delivery</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Areas We Serve -->
        <section class="section" style="background-color: var(--color-base);">
          <div class="container">
            <div class="section-header">
              <h2 class="section-title">Areas We Serve in ${location.name}</h2>
            </div>
            <div class="served-areas-grid">
              ${(location.areas || []).map(area => `
                <div class="served-area-card">
                  ${icons.mapPin}
                  <span>${area}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </section>

        <!-- Local Projects -->
        ${localProjects.length > 0 ? `
        <section class="section" style="background-color: var(--color-surface);">
          <div class="container">
            <div class="section-header">
              <h2 class="section-title">Recent Projects in ${location.name}</h2>
            </div>
            <div class="projects-grid">
              ${localProjects.slice(0, 3).map(p => renderProjectCard(p)).join('')}
            </div>
            <div style="text-align: center; margin-top: var(--space-8);">
              <a href="/projects" class="btn btn-secondary">View All Projects</a>
            </div>
          </div>
        </section>
        ` : `
        <section class="section" style="background-color: var(--color-surface);">
           <div class="container">
             <div class="section-header">
               <h2 class="section-title">Recent Projects in ${location.name}</h2>
               <p>Coming soon...</p>
             </div>
           </div>
        </section>
        `}

        <!-- Contact Section -->
        <section class="section" style="background-color: var(--color-base);">
          <div class="container">
            <div class="location-contact-grid">
              <div class="location-contact-info">
                <h2 class="section-title">Visit Our ${location.name} Office</h2>
                <div class="contact-methods">
                  <div class="contact-method">
                    <div class="contact-method-icon">${icons.mapPin}</div>
                    <div>
                      <p class="contact-method-label">Address</p>
                      <p class="contact-method-value">${location.address || 'Contact us for address'}</p>
                    </div>
                  </div>
                  <div class="contact-method">
                    <div class="contact-method-icon">${icons.phone}</div>
                    <div>
                      <p class="contact-method-label">Phone</p>
                      <p class="contact-method-value">
                        <a href="tel:${(location.phone || '').replace(/\s/g, '')}">${location.phone || 'Contact us'}</a>
                      </p>
                    </div>
                  </div>
                </div>
                <div class="quick-contact" style="margin-top: var(--space-6);">
                  <a href="https://wa.me/${(location.phone || '').replace(/[^0-9]/g, '')}?text=Hi, I'm interested in interior design services in ${location.name}" 
                     class="quick-contact-btn quick-contact-whatsapp" target="_blank">
                    ${icons.messageCircle}
                    WhatsApp
                  </a>
                  <a href="tel:${(location.phone || '').replace(/\s/g, '')}" class="quick-contact-btn quick-contact-call">
                    ${icons.phone}
                    Call Now
                  </a>
                </div>
              </div>
              <div class="location-map">
                <iframe src="${location.map_url || location.mapUrl || ''}" allowfullscreen="" loading="lazy"></iframe>
              </div>
            </div>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="section cta-section">
          <div class="container">
            <div class="cta-content">
              <h2 class="cta-title">Ready for Your ${location.name} Home?</h2>
              <p class="cta-subtitle">
                Book a free consultation with our local design experts.
              </p>
              <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">
                Book Free Consultation
              </button>
            </div>
          </div>
        </section>
      `;

  } catch (error) {
    console.error('Location page error:', error);
    container.innerHTML = `
        <section class="page-header">
          <div class="container">
            <div class="page-header-content">
              <h1 class="page-title">Location Not Found</h1>
              <a href="/locations" class="btn btn-primary mt-6">View All Locations</a>
            </div>
          </div>
        </section>
      `;
  }
}


